import React from 'react';
import { connect } from 'react-redux';

import { RMWContext } from 'app/shared/providers/RMWContext';

import billingUtils from 'app/shared/utils/billingUtils';

import BillingActions from 'app/shared/actions/BillingActions';

// components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import TableMFH from 'app/shared/components/TableMFH';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// constants
import { INVOICE_PAYMENTS } from 'app/shared/constants/RouteConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

import {
    Text,
    TextButton,
} from '@zillow/constellation';

const groupPaymentMethods = (paymentMethods) => {
    const accountGroupings = {};
    paymentMethods.forEach((paymentMethod) => {
        if (accountGroupings[paymentMethod.accountNumber]) {
            accountGroupings[paymentMethod.accountNumber].paymentMethods.push(paymentMethod);
        } else {
            accountGroupings[paymentMethod.accountNumber] = {
                accountName: paymentMethod.accountName,
                accountNumber: paymentMethod.accountNumber,
                accountAutopay: paymentMethod.accountAutopay,
                paymentMethods: [paymentMethod],
            };
        }
        if (paymentMethod.defaultPaymentMethod) {
            accountGroupings[paymentMethod.accountNumber].defaultPaymentMethod = paymentMethod;
        }
    });
    return Object.values(accountGroupings);
};

const PaymentSettingsPage = ({
    dispatch,
    loadingState,
    paymentMethods,
}) => {
    const { paths } = React.useContext(RMWContext);
    React.useEffect(() => {
        dispatch(BillingActions.getPaymentMethodsForUser());
        return () => dispatch(BillingActions.paymentMethodsInit());
    }, []);

    const handleEditClick = (accountNumber) => {
        window.router.transitionTo(`${paths.billingPage}/account/${accountNumber}`);
    };

    const renderPaymentMethod = paymentMethod => (
        <React.Fragment>
            <Text>
                {billingUtils.formatPaymentMethod(paymentMethod)}
            </Text>
            <br />
            <Text>
                {`Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`}
            </Text>
        </React.Fragment>
    );

    const columnConfig = [
        {
            width: 3,
            header: 'Account',
            renderer: paymentMethod => paymentMethod.accountName,
            align: 'left',
        },
        {
            width: 2,
            header: 'Next Payment Date',
            renderer: account => (account.accountAutopay ? billingUtils.getNextAutoPaymentDate() : '-'),
            align: 'left',
        },
        {
            width: 2,
            header: 'Payment Method',
            renderer: (account) => {
                if (account.paymentMethods.length > 1) {
                    return 'Multiple';
                }
                if (account.defaultPaymentMethod) {
                    return renderPaymentMethod(account.defaultPaymentMethod);
                }
                return renderPaymentMethod(account.paymentMethods[0]);
            },
            align: 'left',
        },
        {
            width: 2,
            header: 'Auto Pay',
            renderer: account => (account.accountAutopay ? 'ON' : 'OFF'),
            align: 'left',
        },
        {
            width: 1,
            header: '',
            renderer: account => (
                <TextButton onClick={() => handleEditClick(account.accountNumber)}>Edit</TextButton>
            ),
            align: 'center',
        },
    ];

    const groupedPaymentMethods = groupPaymentMethods(paymentMethods);

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <PermissionCheck route={INVOICE_PAYMENTS}>
                <div className="PaymentSettingsPage">
                    <HeadWrapper title="Payment Settings" />
                    <TableMFH
                        columnConfig={columnConfig}
                        data={groupedPaymentMethods}
                        isLoading={loadingState === LoadingConstants.LOADING}
                        edgePadding={14}
                    />
                </div>
            </PermissionCheck>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    loadingState: state.billing.paymentMethods.loadingState,
    paymentMethods: state.billing.paymentMethods.paymentMethods,
});

export default connect(mapStateToProps)(PaymentSettingsPage);
